/**
 * Copyright (c) 2019, Tidepool Project
 *
 * This program is free software; you can redistribute it and/or modify it under
 * the terms of the associated License, which is identical to the BSD 2-Clause
 * License as published by the Open Source Initiative at opensource.org.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS
 * FOR A PARTICULAR PURPOSE. See the License for more details.
 *
 * You should have received a copy of the License along with this program; if
 * not, you can obtain one from Tidepool Project at tidepool.org.
 */

import React from 'react';
import { Button, TextField, Table, TableBody, TableCell, TableHead, TableRow, InputAdornment } from '@material-ui/core';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import shortid from 'shortid';
var utils = require('../../../Utils.js');

/*
 *  TimeValuePicker is a componenent which handles the tables for insulin to carb ratios, basal rates, and sensitivity rates
 *  It is a dynamically changing table with guardrails and warnings. Delete row functionality is included.
 */

class TimeValuePicker extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
    // Allows for values to be passed in, or will start from scratch if no values provided
    this.state.values = props.values
      ? props.values
      : [
          {
            id: shortid.generate(),
            time: '00:00',
            val: utils.getInitialValue(this.props.tableType)
            // isFirst: true
          }
        ];
    // function below updates the values in the formik state handler
    this.props.setFieldValue(this.props.fieldType, this.state.values);
  }

  // handles row deletion
  handleRowDel(value) {
    var index = this.state.values.indexOf(value);
    this.state.values.splice(index, 1);
    this.setState(this.state.values.sort(utils.compare));
    // function below updates the values in the formik state handler
    this.props.setFieldValue(this.props.fieldType, this.state.values);
  }

  // handles row addition
  handleAddEvent(evt) {
    var nextTime = this.state.values.reduce((max, p) => (p.time > max ? p.time : max), this.state.values[0].time);
    var nextMinutes = (parseInt(nextTime.slice(3, 5)) + 30) % 60;
    var nextHour =
      parseInt(nextTime.slice(3, 5)) + 30 >= 60 ? parseInt(nextTime.slice(0, 2)) + 1 : parseInt(nextTime.slice(0, 2));
    nextHour = ('0' + nextHour).slice(-2);
    nextMinutes = ('0' + nextMinutes).slice(-2);

    var value = {
      id: shortid.generate(),
      time: nextHour + ':' + nextMinutes,
      val: utils.getLastValue(this.state.values, this.props.tableType)
      // isFirst: false
    };
    this.state.values.push(value);
    this.setState(this.state.values.sort(utils.compare));
    // function below updates the values in the formik state handler
    this.props.setFieldValue(this.props.fieldType, this.state.values);
  }

  // handles any change of value within the table
  handleProductTable(evt) {
    var item = {
      id: evt.target.id,
      name: evt.target.name,
      value: evt.target.value
    };
    var values = this.state.values.slice();
    var newValues = values.map(function(value) {
      for (var key in value) {
        if (key === item.name && value.id === item.id) {
          value[key] = item.value;
        }
      }
      return value;
    });
    this.setState({ values: newValues.sort(utils.compare) });
    // function below updates the values in the formik state handler
    this.props.setFieldValue(this.props.fieldType, this.state.values);
  }

  render() {
    return (
      <ProductTable
        onProductTableUpdate={this.handleProductTable.bind(this)}
        onRowAdd={this.handleAddEvent.bind(this)}
        onRowDel={this.handleRowDel.bind(this)}
        products={this.state.values}
        tableType={this.props.tableType}
        addAnAdditionalText={this.props.addAnAdditionalText}
        errors={this.props.errors}
        touched={this.props.touched}
      />
    );
  }
}

// helper component which is a wrapper for the whole value table
class ProductTable extends React.Component {
  render() {
    var onProductTableUpdate = this.props.onProductTableUpdate;
    var rowDel = this.props.onRowDel;
    var len = this.props.products.length;
    var tableType = this.props.tableType;
    var touched = this.props.touched;
    var errors = this.props.errors;
    var loopValues = this.props.products.map((loopValue, index) => {
      return (
        <ProductRow
          onProductTableUpdate={onProductTableUpdate}
          product={loopValue}
          length={len}
          onDelEvent={rowDel.bind(this)}
          key={loopValue.id}
          tableType={tableType}
          errors={errors}
          touched={touched}
          index={index}
        />
      );
    });

    return (
      <div style={{ width: '100%' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left">Start Time</TableCell>
              <TableCell align="left">{utils.getColumnIdentifier(this.props.tableType)}</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{loopValues}</TableBody>
        </Table>
        <Button
          onClick={this.props.onRowAdd}
          fullWidth
          color="primary"
          variant="text"
          style={{ textTransform: 'initial' }}
        >
          {utils.getAdditionalButtonName(this.props.tableType)}
        </Button>
      </div>
    );
  }
}

// helper component which comprises a single row
class ProductRow extends React.Component {
  onDelEvent() {
    this.props.onDelEvent(this.props.product);
  }

  render() {
    return (
      <TableRow>
        <TimeCell
          onProductTableUpdate={this.props.onProductTableUpdate}
          cellData={{
            type: 'time',
            value: this.props.product.time,
            id: this.props.product.id,
            isFirst: this.props.product.isFirst
          }}
          errors={this.props.errors}
          touched={this.props.touched}
        />
        <ValueCell
          onProductTableUpdate={this.props.onProductTableUpdate}
          cellData={{
            type: 'val',
            value: this.props.product.val,
            id: this.props.product.id
          }}
          tableType={this.props.tableType}
          errors={this.props.errors}
          touched={this.props.touched}
        />
        <TableCell align="right" padding="checkbox">
          {this.props.index === 0 ? (
            <Button disabled>
              <DeleteOutline color="disabled" />
            </Button>
          ) : (
            <Button onClick={this.onDelEvent.bind(this)}>
              <DeleteOutline color="primary" />
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  }
}

// helper component which comprises a time picker
class TimeCell extends React.Component {
  render() {
    return (
      <TableCell align="left" size="small">
        <TextField
          variant="outlined"
          type="time"
          InputProps={{
            readOnly: this.props.cellData.isFirst
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={{ step: 1800 }}
          fullWidth
          // style={{maxWidth: 225}}
          name={this.props.cellData.type}
          id={this.props.cellData.id}
          value={this.props.cellData.value}
          onChange={this.props.onProductTableUpdate}
          error={this.props.touched && Boolean(this.props.errors)}
          helperText={this.props.touched ? this.props.errors : ''}
        />
      </TableCell>
    );
  }
}

// helper component which comprises a value picker
class ValueCell extends React.Component {
  render() {
    return (
      <TableCell align="right">
        <TextField
          variant="outlined"
          type="number"
          fullWidth
          // style={{maxWidth: 230}}
          name={this.props.cellData.type}
          id={this.props.cellData.id}
          value={this.props.cellData.value}
          onChange={this.props.onProductTableUpdate}
          InputProps={{
            endAdornment: <InputAdornment position="end">{utils.unitsHelper(this.props.tableType)}</InputAdornment>,
            style: { color: utils.timeValuePickerWarnings(this.props.cellData.value, this.props.tableType).color }
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={utils.barriers(this.props.tableType)}
          FormHelperTextProps={{
            style: { color: utils.timeValuePickerWarnings(this.props.cellData.value, this.props.tableType).color }
          }}
          error={this.props.touched && Boolean(this.props.errors)}
          helperText={utils.helperTextPicker(
            this.props.touched,
            this.props.errors,
            utils.timeValuePickerWarnings(this.props.cellData.value, this.props.tableType)
          )}
        />
      </TableCell>
    );
  }
}

export default TimeValuePicker;
